import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Avatar } from "@/components/Avatar";
import { Activity, Building2, KeyRound, ServerCog, ScrollText, Menu, LogOut, CreditCard, Globe2, HardDriveDownload, LifeBuoy, Clapperboard, LayoutGrid, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { CommandPalette } from "@/components/CommandPalette";
import { PLATFORM_ACTIONS } from "@/lib/shortcuts";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { initTheme } from "@/lib/theme";
import { LanguageSwitcher } from "./AppShell";
import { BrandLogo } from "@/components/BrandLogo";


const NAV = [
  { to: "/platform-admin", key: "nav.platform.dashboard", icon: Activity, id: "nav-platform-dashboard", end: true },
  { to: "/platform-admin/customers", key: "nav.platform.customers", icon: Building2, id: "nav-platform-customers" },
  { to: "/platform-admin/licenses", key: "nav.platform.licenses", icon: KeyRound, id: "nav-platform-licenses" },
  { to: "/platform-admin/billing", key: "nav.platform.billing", icon: CreditCard, id: "nav-platform-billing" },
  { to: "/platform-admin/development", key: "nav.platform.development", icon: Wrench, id: "nav-platform-development" },
  { to: "/platform-admin/system", key: "nav.platform.system", icon: ServerCog, id: "nav-platform-system" },
  { to: "/platform-admin/backup", key: "nav.platform.backup", icon: HardDriveDownload, id: "nav-platform-backup" },
  { to: "/platform-admin/docs", key: "nav.platform.docs", icon: HardDriveDownload, id: "nav-platform-docs", label: "Documentation" },
  { to: "/platform-admin/promo", key: "nav.platform.promo", icon: Clapperboard, id: "nav-platform-promo" },
  { to: "/platform-admin/catalog", key: "nav.platform.catalog", icon: LayoutGrid, id: "nav-platform-catalog" },
  { to: "/platform-admin/industry-templates", key: "nav.platform.industry_templates", icon: LayoutGrid, id: "nav-platform-industry-templates" },
  { to: "/platform-admin/recovery", key: "nav.platform.recovery", icon: LifeBuoy, id: "nav-platform-recovery" },
  { to: "/platform-admin/website", key: "nav.platform.website", icon: Globe2, id: "nav-platform-website", newTab: true },
  { to: "/platform-admin/audit", key: "nav.platform.audit", icon: ScrollText, id: "nav-platform-audit" },
];

function PlatformPalette() {
  const { t } = useT();
  return <CommandPalette scope="platform" screens={NAV.map((n) => ({ to: n.to, id: n.id, icon: n.icon, label: t(n.key) }))} actions={PLATFORM_ACTIONS} home="/platform-admin" />;
}

function Nav({ onNavigate }) {
  const { t } = useT();
  return (
    <nav className="flex flex-col gap-0.5 px-3 flex-1 overflow-y-auto min-h-0 pb-2" data-testid="platform-sidebar">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.id} onClick={onNavigate} target={n.newTab ? "_blank" : undefined} rel={n.newTab ? "noopener" : undefined} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}><n.icon className="h-4 w-4" />{t(n.key)}</NavLink>
      ))}
    </nav>
  );
}

export default function PlatformShell({ children }) {
  const { me, logout } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  useEffect(() => { document.title = "Platform Administration · Orbis ERP"; initTheme("binance"); }, []);
  return (
    <div className="min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col sidebar grain sticky top-0 h-screen overflow-hidden border-r border-white/10">
        <div className="px-5 h-14 flex items-center border-b border-white/10"><BrandLogo name="Orbis Platform" tone="platform" size="sm" testId="platform-logo" /></div>
        <div className="px-5 py-4"><span className="bg-teal-500/15 text-teal-300 border border-teal-500/30 text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold" data-testid="platform-badge">{t("platform.badge")}</span></div>
        <Nav />
        <div className="shrink-0 p-4 border-t border-white/10 text-xs text-slate-400 flex items-center gap-2 min-w-0"><Avatar userId={me?.user?.id} v={me?.user?.avatar_v} name={me?.user?.name} size={28} testId="platform-avatar" /><span className="truncate">{me?.user?.email}</span></div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b sticky top-0 z-40 bg-card/95 backdrop-blur-md px-3 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-2"><Button variant="ghost" size="icon" className="lg:hidden" data-testid="platform-mobile-menu" onClick={() => setOpen(true)}><Menu className="h-5 w-5" /></Button><span className="text-sm text-muted-foreground hidden sm:inline">Orbis ERP · Platform Administration · <span className="font-mono text-xs">{me?.platform_role}</span></span></div>
          <div className="flex items-center gap-2"><PlatformPalette /><ThemeSwitcher fallback="binance" /><LanguageSwitcher /><Button variant="ghost" size="sm" data-testid="platform-logout-btn" onClick={logout}><LogOut className="h-4 w-4 mr-1" />{t("common.logout")}</Button></div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full mx-auto min-w-0 overflow-x-hidden">{children}</main>
      </div>
      <Sheet open={open} onOpenChange={setOpen}><SheetContent side="left" className="bg-[#071a19] text-slate-100 w-72 p-0 border-r-0"><div className="px-5 h-14 flex items-center border-b border-white/10"><BrandLogo name="Orbis Platform" tone="platform" size="sm" testId="platform-logo-mobile" /></div><div className="py-4"><Nav onNavigate={() => setOpen(false)} /></div></SheetContent></Sheet>
    </div>
  );
}
